import { useQuery } from "@tanstack/react-query";

import type { IDurationData } from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { DefaultFetchService } from "app/services/fetchService";

import {
    END_POINT_PATH,
    END_POINT_PATH_MMX_BETA_BAR_CHART,
    END_POINT_PATH_MOBILE,
    END_POINT_PATH_TOTAL,
    END_POINT_PATH_TOTAL_POP,
    EXCEL_END_POINT_PATH,
    EXCEL_END_POINT_PATH_MMX_BETA,
    EXCEL_END_POINT_PATH_MOBILE,
    EXCEL_END_POINT_PATH_TOTAL,
} from "./consts";

const fetchService = DefaultFetchService.getInstance();

interface UseChannelDataOptions {
    params: any;
    durationObject: IDurationData;
    isPeriodOverPeriod: boolean;
    isMTDActive?: boolean;
    granularity?: { value: string };
    withExcel?: boolean;
    isMmxBeta?: boolean;
    domainsKeys?: string;
}

export const useChannelData = ({
    params,
    durationObject,
    isPeriodOverPeriod,
    isMTDActive = false,
    granularity,
    withExcel = false,
    isMmxBeta = false,
    domainsKeys,
}: UseChannelDataOptions) => {
    const webSource = params.webSource;
    const queryString = buildQueryParamsString(params, durationObject, isMTDActive, granularity, domainsKeys);

    const endpointUrl = getEndpointUrl(webSource, isPeriodOverPeriod, queryString, isMmxBeta);

    const { data, isLoading } = useQuery({
        queryKey: ["trafficSourcesBarChart", webSource, queryString],
        queryFn: () => fetchService.get(endpointUrl),
        placeholderData: (prev) => prev,
        staleTime: 1000 * 60 * 5,
    });
    const excelUrl = () => (withExcel ? getExcelUrl(webSource, queryString, isMmxBeta) : null);

    return withExcel ? { data, isLoading, getExcelUrl: excelUrl } : { data, isLoading };
};
const buildQueryParamsString = (
    params,
    durationObject,
    isMTDActive?: boolean,
    granularity?: { value: string },
    domainsKeys?: string,
): string => {
    const { key, isWWW, country, webSource, MMXBeta } = params;
    const { from, to, isWindow, compareFrom, compareTo } = durationObject.forAPI;
    const query = new URLSearchParams({
        includeSubDomains: JSON.stringify(isWWW === "*"),
        keys: domainsKeys ?? key,
        from,
        to,
        country,
        webSource,
        isWindow: JSON.stringify(isWindow),
    });

    if (granularity?.value) {
        query.set("timeGranularity", granularity.value);
    }
    if (isMTDActive) {
        query.set("latest", "l");
    }
    if (compareFrom && compareTo) {
        query.set("compareFrom", compareFrom);
        query.set("compareTo", compareTo);
    }
    if (MMXBeta) {
        query.set("MMXBeta", MMXBeta);
    }
    return query.toString();
};

const getEndpointUrl = (
    webSource: string,
    isPeriodOverPeriod: boolean,
    queryString: string,
    isMmxBeta?: boolean,
): string => {
    if (isMmxBeta) {
        return `${END_POINT_PATH_MMX_BETA_BAR_CHART}?${queryString}`;
    }
    if (webSource === devicesTypes.TOTAL) {
        return `${isPeriodOverPeriod ? END_POINT_PATH_TOTAL_POP : END_POINT_PATH_TOTAL}?${queryString}`;
    }
    if (webSource === devicesTypes.MOBILE) {
        return `${END_POINT_PATH_MOBILE}?${queryString}`;
    }
    return `${END_POINT_PATH}?${queryString}`;
};

export const getMmxExcelUrl = (
    params: { country: string; isWWW: string; key: string; webSource: string },
    durationObject: IDurationData,
    timeGranularity: string,
): string =>
    getExcelUrl(params.webSource, buildQueryParamsString(params, durationObject, false, { value: timeGranularity }));

const getExcelUrl = (webSource: string, queryString: string, isMmxBeta?: boolean): string => {
    if (webSource === devicesTypes.TOTAL && isMmxBeta) {
        return `${EXCEL_END_POINT_PATH_MMX_BETA}?${queryString}`;
    }
    if (webSource === devicesTypes.TOTAL) {
        return `${EXCEL_END_POINT_PATH_TOTAL}?${queryString}`;
    }
    if (webSource === devicesTypes.MOBILE) {
        return `${EXCEL_END_POINT_PATH_MOBILE}?${queryString}`;
    }
    return `${EXCEL_END_POINT_PATH}?${queryString}`;
};
